/**
 * Solve from cached prices immediately on a chain switch, then at most 500 ms
 * after a price update. Each pass shares one forward and one expiry clock.
 * Known values survive transient missing quotes within the same market context.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  forwardFromParity,
  greeksOf,
  legGreeks,
  probabilityItm,
  yearsToExpiry,
  type Greeks,
  type LegGreeks,
} from '@apna/shared'
import { tickStore } from '@/feed/tickStore'

/** Maximum interval between price-driven Greek updates. */
export const GREEKS_CADENCE_MS = 500

/**
 * The risk-free rate, as a constant.
 *
 * India's 91-day T-bill has sat near 6.5% through 2026. It is a CONSTANT and
 * not a configured value because at the tenors a weekly option trades —
 * three to seven days — the rate changes the price in the third decimal
 * place: the discount factor over seven days at 6.5% is 0.99875, and moving
 * the rate a whole point moves it to 0.99856. Making it configurable would
 * invite someone to tune it and believe the result changed something.
 *
 * It matters at quarterly tenors, which this chain does not show. If long
 * expiries are ever listed here, this becomes a real input.
 */
export const RISK_FREE_RATE = 0.065

export interface GreeksRow {
  readonly strike: number
  readonly ceToken: number | null
  readonly peToken: number | null
}

/** What one solved option carries. */
export interface SolvedLeg {
  readonly iv: number
  readonly greeks: Greeks
  /**
   * N(d2) — the risk-neutral chance of finishing in the money, as a fraction.
   *
   * Solved in the SAME pass as the greeks, off the same forward and the same
   * clock, for the reason the pass exists at all: computed per cell it would
   * be the row above and the row below answering with different times to
   * expiry, and the difference would be an artefact of render order.
   */
  readonly pItm: number | null
}

export interface ChainGreeks {
  /** Keyed by broker token. Absent means this option could not be solved. */
  readonly byToken: ReadonlyMap<number, SolvedLeg>
  /** Why a token has no entry, for a tooltip. */
  readonly refusals: ReadonlyMap<number, string>
  /** The forward this pass used, from put-call parity at the money. */
  readonly forward: number | null
  /** When the pass ran. Null before the first one. */
  readonly computedAt: number | null
  /** Years to expiry at the time of the pass. */
  readonly years: number
}

const EMPTY: ChainGreeks = {
  byToken: new Map(),
  refusals: new Map(),
  forward: null,
  computedAt: null,
  years: 0,
}

/**
 * One pass over the chain. Pure apart from reading the tick store.
 *
 * Exported for its own test: the cadence is a timer and the arithmetic is
 * not, and the arithmetic is the part worth asserting on.
 */
export function solveChain(
  rows: readonly GreeksRow[],
  opts: { readonly years: number; readonly atmStrike: number | null; readonly now: number },
): ChainGreeks {
  const byToken = new Map<number, SolvedLeg>()
  const refusals = new Map<number, string>()

  /*
   * `slotFor`, NOT `track`. This is a read path, and `track` RESERVES a slot
   * from a fixed capacity that is never reclaimed — a solve pass running
   * every five seconds over a widening window would spend the pool on
   * instruments it is only reading. A token with no slot has no price, which
   * is the same answer and costs nothing.
   */
  const ltpOf = (token: number | null): number | null => {
    if (token === null || token <= 0) return null
    const ltp = tickStore.field(tickStore.slotFor(token), 'ltp')
    // Zero is the store's "nothing yet", never a price.
    return ltp > 0 ? ltp : null
  }

  /**
   * The middle of the live book, when there is one — ROADMAP 9.19.
   *
   * A CROSSED BOOK IS NOT A PRICE. `ask < bid` happens on a thin wing during
   * a fast move and its midpoint is a number with no market behind it, so it
   * is refused rather than averaged.
   */
  const midOf = (token: number | null): number | null => {
    if (token === null || token <= 0) return null
    const slot = tickStore.slotFor(token)
    const bid = tickStore.field(slot, 'bid')
    const ask = tickStore.field(slot, 'ask')
    if (!(bid > 0) || !(ask > 0) || ask < bid) return null
    return (bid + ask) / 2
  }

  if (opts.years <= 0 || opts.atmStrike === null) {
    /*
     * A SETTLED EXPIRY IS A REASON, NOT AN ABSENCE.
     *
     * `years <= 0` means the contract has already settled — on expiry day
     * after 15:30 IST, which is exactly when a desk is most likely to be
     * staring at the chain wondering what broke. Every price and open
     * interest is still on screen, because those are historical facts that
     * survive settlement; volatility is not, so there is nothing to solve
     * and every cell is a dash.
     *
     * Returning a bare EMPTY made that indistinguishable from a dead feed.
     * Reported on 2026-10-27: "even when OI and LTP data is there, still the
     * greeks aren't coming". They had expired two hours earlier.
     */
    const why =
      opts.years <= 0
        ? 'This expiry has settled. An expired option has no time value, so there is no volatility to solve for.'
        : 'No at-the-money strike yet, so there is no straddle to take the forward from.'
    for (const row of rows) {
      for (const token of [row.ceToken, row.peToken]) {
        if (token !== null && token > 0) refusals.set(token, why)
      }
    }
    return { ...EMPTY, refusals, years: opts.years, computedAt: opts.now }
  }

  /*
   * THE FORWARD COMES FROM THE CHAIN ITSELF.
   *
   * Taken from the straddle nearest the money, where both legs are liquid
   * and the parity difference is two large numbers rather than two small
   * ones. Without it the alternative is the cash index plus an assumed
   * carry, which gives every call one IV and every put another and prints a
   * skew that is not in the market — see `greeks.ts`.
   */
  const atmRow = rows.find((r) => r.strike === opts.atmStrike)
  const callPrice = ltpOf(atmRow?.ceToken ?? null)
  const putPrice = ltpOf(atmRow?.peToken ?? null)
  const forward =
    callPrice !== null && putPrice !== null
      ? forwardFromParity({
          callPrice,
          putPrice,
          strike: opts.atmStrike,
          years: opts.years,
          rate: RISK_FREE_RATE,
        })
      : null

  if (forward === null) {
    /*
     * No forward, no IV for ANY strike. Falling back to the spot would
     * produce a full chain of plausible numbers that are wrong by the basis
     * in opposite directions on the two sides — the one failure mode this
     * module is built to avoid, and it would be invisible.
     */
    for (const row of rows) {
      for (const token of [row.ceToken, row.peToken]) {
        if (token !== null && token > 0) {
          refusals.set(token, 'No at-the-money straddle price yet, so there is no forward to solve against.')
        }
      }
    }
    return { byToken, refusals, forward: null, computedAt: opts.now, years: opts.years }
  }

  /** Tokens that produced a quote, whether or not it could be solved. */
  const quoted = new Set<number>()

  const record = (token: number | null, optionType: 'CE' | 'PE', strike: number): void => {
    if (token === null || token <= 0) return
    const price = ltpOf(token)
    if (price === null) {
      refusals.set(token, 'No price yet.')
      return
    }
    /*
     * From here the contract HAS a quote, which is what later qualifies it
     * for the mirror pass (`quoted`). A strike with no price at all never
     * borrows one:
     * a row of greeks under an empty LTP would be this module inventing a
     * contract, not rescuing one.
     */
    quoted.add(token)
    const solve = (at: number): LegGreeks =>
      legGreeks({ price: at, forward, strike, years: opts.years, rate: RISK_FREE_RATE, optionType })

    let r: LegGreeks = solve(price)

    /*
     * ── WHEN THE LAST TRADE IS TOO OLD TO BE A PRICE — ROADMAP 9.19 ──────
     *
     * A deep wing trades a handful of times a session, so its `ltp` is a
     * HISTORICAL FACT that can be an hour old while the book beside it is
     * live. When the forward has moved since that trade, the stale number
     * falls under the option's own intrinsic value and `impliedVol` refuses
     * it — correctly: there is no volatility that reaches it.
     *
     * The old behaviour was a row of dashes, and worse, a row that had been
     * showing greeks a second earlier: the retention rule below keeps a
     * previous value only for a refusal beginning "No ", and these two do
     * not, so the cells BLANKED. Reported on 2026-11-03 as "some of these
     * legs get blanked when the LTP is kind of stuck", and the chain in that
     * screenshot shows the cause plainly — adjacent strikes solving to 50.6,
     * 60.9, 190.8, 44.0, 123.9 and 182.5 percent, which is not a skew, it is
     * six last trades from six different moments.
     *
     * So when the trade price cannot be solved, the live midpoint is tried.
     * It is a rescue and NOT a preference: a quote that solves is always
     * used as-is, because the IV on screen must be the one implied by the
     * price printed beside it. Where the mid is used instead, the tooltip
     * says so rather than letting the two silently disagree.
     */
    let markedFromMid = false
    if (r.kind !== 'OK') {
      const mid = midOf(token)
      if (mid !== null && mid !== price) {
        const fromMid = solve(mid)
        if (fromMid.kind === 'OK') {
          r = fromMid
          markedFromMid = true
        }
      }
    }

    if (r.kind === 'OK') {
      if (markedFromMid) {
        /*
         * A NOTE, not a refusal. `refusals` carries the tooltip for a cell
         * whether or not it has a value — the retention branch below already
         * relies on that — and a number whose provenance is hidden is the
         * thing this module exists to avoid.
         */
        refusals.set(
          token,
          'Solved from the bid/ask midpoint: the last traded price is stale and sits below this option\'s intrinsic value.',
        )
      }
      byToken.set(token, {
        iv: r.iv,
        greeks: r.greeks,
        // The SOLVED vol, not a guess: the probability has to be the one
        // implied by the price actually on screen, or it disagrees with the
        // IV printed beside it.
        pItm: probabilityItm({
          forward,
          strike,
          years: opts.years,
          rate: RISK_FREE_RATE,
          vol: r.iv,
          optionType,
        }),
      })
    } else refusals.set(token, r.why)
  }

  for (const row of rows) {
    record(row.ceToken, 'CE', row.strike)
    record(row.peToken, 'PE', row.strike)
  }

  /*
   * ── THE MIRROR STRIKE, WHICH IS NOT A FALLBACK BUT THE BETTER PRICE ────
   *
   * PUT-CALL PARITY GIVES BOTH SIDES OF A STRIKE THE SAME IMPLIED
   * VOLATILITY. That is not an approximation or a smoothing choice: it is
   * the arbitrage that defines the pair. C - P = DF x (F - K) holds exactly,
   * so any vol that prices one prices the other.
   *
   * One side of every strike is DEEP IN THE MONEY and the other is deep out.
   * The in-the-money side is where the liquidity is not: it trades a handful
   * of times a session, its last trade goes stale, and once the forward
   * moves past it the stale number sits under intrinsic and cannot be
   * solved. The out-of-the-money side at the SAME strike is the liquid one
   * and solves cleanly.
   *
   * Measured from the chain reported on 2026-11-03. The 23,350 straddle puts
   * the forward at 23,321 (73.65 - 102.60 + 23,350), and every blank put was
   * exactly a trade below its own intrinsic:
   *
   *       strike   intrinsic   last trade   time value
   *       23,700       379.0       383.05       +4.05   solved, IV 16.8
   *       23,750       429.0       425.15       -3.80   BLANK
   *       23,800       479.0       481.00       +2.05   solved, IV 18.0
   *       23,850       529.0       524.70       -4.25   BLANK
   *       23,900       579.0       574.00       -5.00   BLANK
   *       23,950       629.0       621.50       -7.45   BLANK
   *
   * Every one of those strikes had a CALL that solved — 17.8, 19.7, 20.8,
   * 21.7 — so the answer was on the same row the whole time.
   *
   * THIS RUNS LAST, so a side that solved from its own price always keeps
   * it. And it is only ever reached when the strike's own quote could not
   * be solved at all, which is why it cannot quietly smooth away a real
   * dislocation: there is no number of its own to disagree with.
   */
  for (const row of rows) {
    for (const [token, mirror, optionType] of [
      [row.ceToken, row.peToken, 'CE'],
      [row.peToken, row.ceToken, 'PE'],
    ] as const) {
      if (token === null || token <= 0 || byToken.has(token)) continue
      // A QUOTE THAT COULD NOT BE SOLVED, never a missing one — see `priced`.
      if (!quoted.has(token)) continue
      if (mirror === null || mirror <= 0) continue
      const other = byToken.get(mirror)
      if (!other) continue

      const priced = { forward, strike: row.strike, years: opts.years, rate: RISK_FREE_RATE, optionType }
      byToken.set(token, {
        iv: other.iv,
        greeks: greeksOf({ ...priced, vol: other.iv }),
        pItm: probabilityItm({ ...priced, vol: other.iv }),
      })
      refusals.set(
        token,
        `Volatility taken from the ${row.strike.toLocaleString('en-IN')} ` +
          `${optionType === 'CE' ? 'put' : 'call'} at this strike — put-call parity gives both the ` +
          'same implied volatility, and this side\'s own last trade is below its intrinsic value.',
      )
    }
  }

  return { byToken, refusals, forward, computedAt: opts.now, years: opts.years }
}

/**
 * Greeks for the visible chain, refreshed on `GREEKS_CADENCE_MS`.
 *
 * Switches and first quotes solve immediately. The interval bounds normal
 * updates to 500 ms, and `refresh` requests an immediate pass.
 */
/**
 * A delta reader for the strategy resolver — ROADMAP 5.23.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SOLVED SURFACE, NOT A SECOND SOLVER
 *
 * `CLOSEST_DELTA` and `DELTA_RANGE` need a delta per contract, and the
 * resolver deliberately will not compute one: it takes a `deltaOf` function
 * instead. This is that function, built over the pass the chain column is
 * already showing.
 *
 * Deriving it here rather than solving again is not only about cost. A
 * second `useChainGreeks` would call `tickStore.track` for every contract,
 * and `track` RESERVES a slot from a fixed capacity that is never reclaimed
 * — the same budget narrowing the warm chain to 24 strikes was fought for.
 * It would also run on its own clock, free to disagree with the number the
 * trader is reading in the column.
 *
 * SIGNED, because that is what the surface holds. The resolver compares
 * magnitudes; see `CLOSEST_DELTA` for why the stored target is unsigned.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function deltaReaderOf(greeks: ChainGreeks): (token: number) => number | null {
  return (token) => greeks.byToken.get(token)?.greeks.delta ?? null
}

export function useChainGreeks(
  rows: readonly GreeksRow[],
  opts: { readonly expiry: string | null; readonly atmStrike: number | null },
): ChainGreeks & { readonly refresh: () => void } {
  const [revision, setRevision] = useState(0)
  const [clock, setClock] = useState(() => Date.now())
  const slots = useMemo(() => rows.flatMap((row) => [row.ceToken, row.peToken])
    .filter((token): token is number => token !== null && token > 0)
    .map((token) => tickStore.track(token)), [rows])
  const dirty = useRef(false)
  const latest = useRef<ChainGreeks>(EMPTY)
  const epoch = tickStore.marketEpoch()
  // Cache only this mounted terminal, bounded by chain count; a simulation
  // switch invalidates it so synthetic Greeks cannot leak into the live book.
  const cache = useRef({ epoch, entries: new Map<string, ChainGreeks>() })
  const key = `${opts.expiry ?? ''}|${rows.map((row) => `${row.ceToken}:${row.peToken}`).join(',')}`
  const result = useMemo(() => {
    if (cache.current.epoch !== epoch) cache.current = { epoch, entries: new Map() }
    const date = opts.expiry === null ? null : new Date(opts.expiry)
    const now = Date.now()
    const solved = date === null || Number.isNaN(date.getTime()) ? EMPTY : solveChain(rows, {
      years: yearsToExpiry(date, new Date(now)), atmStrike: opts.atmStrike, now,
    })
    const previous = cache.current.entries.get(key)
    // A missing quote is not a new zero or a reason to clear a known Greek.
    // Settled options and mathematically invalid prices retain their refusal.
    if (previous && solved.years > 0) {
      const byToken = new Map(solved.byToken)
      const refusals = new Map(solved.refusals)
      for (const [token, value] of previous.byToken) {
        const why = solved.refusals.get(token)
        if (!byToken.has(token) && why?.startsWith('No ')) {
          byToken.set(token, value)
          refusals.set(token, `Showing the last calculated value. ${why}`)
        }
      }
      const retained = { ...solved, byToken, refusals,
        computedAt: solved.byToken.size === 0 && byToken.size > 0 ? previous.computedAt : solved.computedAt }
      cache.current.entries.delete(key)
      cache.current.entries.set(key, retained)
      return retained
    }
    cache.current.entries.set(key, solved)
    if (cache.current.entries.size > 32) cache.current.entries.delete(cache.current.entries.keys().next().value!)
    return solved
  }, [rows, opts.expiry, opts.atmStrike, revision, clock, epoch, key])
  latest.current = result

  const refresh = useCallback(() => { setRevision((value) => value + 1) }, [])
  useEffect(() => {
    const off = slots.map((slot) => tickStore.subscribeSlot(slot, () => {
      dirty.current = true
      // The first prices must populate the blank cells immediately, without
      // waiting for the 500 ms cadence that serves an already-priced chain.
      if (latest.current.byToken.size < slots.length || cache.current.epoch !== tickStore.marketEpoch()) refresh()
    }))
    const timer = setInterval(() => {
      if (dirty.current) { dirty.current = false; refresh() }
      setClock(Date.now())
    }, GREEKS_CADENCE_MS)
    return () => { for (const release of off) release(); clearInterval(timer) }
  }, [slots, refresh])

  return useMemo(() => ({ ...result, refresh }), [result, refresh])
}
